import { clientEnv } from "../env";

export interface ResearchSource {
  id: string;
  title: string;
  repository: string;
  purpose: string;
}
export interface ResearchDiscovery {
  sourceId: string;
  sourceUrl: string;
  sourceBlobSha: string;
  fetchedAt: string;
  truncated: boolean;
  candidates: { repository: string; title: string; description: string; url: string }[];
}
export interface ResearchAssessment {
  repository: string;
  url: string;
  checkedAt: string;
  status: "needs_review" | "needs_testing" | "not_recommended";
  license: string | null;
  lastPush: string | null;
  stars: number;
  archived: boolean;
  signals: string[];
  requiredChecks: string[];
  integrated: false;
  benchmarkCompleted: false;
  sourceId: string;
  sourceBlobSha: string;
}

async function request<T>(token: string, path: string, signal: AbortSignal, body?: object): Promise<T> {
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (signal.aborted) controller.abort();
  signal.addEventListener("abort", abort, { once: true });
  const timer = window.setTimeout(abort, 25_000);
  try {
    const response = await fetch(clientEnv.apiBaseUrl + "/creator/research" + path, {
      method: body ? "POST" : "GET",
      headers: { Authorization: "Bearer " + token, ...(body ? { "Content-Type": "application/json" } : {}) },
      cache: "no-store",
      signal: controller.signal,
      ...(body ? { body: JSON.stringify(body) } : {})
    });
    if (!response.ok) {
      if (response.status === 401) throw new Error("Inicia sesión de nuevo para consultar la investigación.");
      if (response.status === 403) throw new Error("Esta sección está reservada al Creador.");
      if (response.status === 404) throw new Error("La investigación todavía no está disponible en este servidor.");
      const error = await response.json().catch(() => null) as { error?: string } | null;
      throw new Error(error?.error || "No se pudo completar la consulta. Vuelve a intentarlo.");
    }
    return await response.json() as T;
  } catch (error) {
    if (controller.signal.aborted && !signal.aborted) {
      throw new Error("La consulta ha tardado demasiado. Vuelve a intentarlo.");
    }
    throw error;
  } finally {
    window.clearTimeout(timer);
    signal.removeEventListener("abort", abort);
  }
}

export const fetchResearchSources = (token: string, signal: AbortSignal) =>
  request<{ schemaVersion: number; sources: ResearchSource[] }>(token, "/sources", signal);
export const discoverResearch = (token: string, sourceId: string, signal: AbortSignal) =>
  request<ResearchDiscovery>(token, "/discover", signal, { sourceId });
export const assessResearch = (token: string, sourceId: string, repository: string, signal: AbortSignal) =>
  request<ResearchAssessment>(token, "/assess", signal, { sourceId, repository });
